/**
 * Hero copy fallback — intentionally brand-agnostic. The real text comes
 * from /api/public/hero; this only kicks in when an editor hasn't set
 * the title or subtitle yet.
 */
export const HERO_FALLBACK_TITLE =
  "Made-to-measure\njourneys, <em>thoughtfully</em>\nplanned.";

export function parseHeroTitle(input: string | undefined): { html: string } {
  // Accept either raw text or text with <em>…</em> emphasis spans from the CMS.
  // Newlines render as <br /> so editors can shape the title without HTML.
  const raw = (input ?? HERO_FALLBACK_TITLE).trim();
  const escaped = raw
    .replace(/&/g, "&amp;")
    .replace(/<(?!\/?em(?:>|\s))/gi, "&lt;")
    .replace(/\n/g, "<br />");
  return { html: escaped };
}

export function formatPrice(
  amount: number | null | undefined,
  currency = "USD",
): string {
  if (amount == null || Number.isNaN(amount) || amount <= 0) return "";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${currency} ${amount}`;
  }
}

/**
 * Exact money for financial documents (invoices/receipts): always renders,
 * including zero and non-integer amounts, with 2 decimal places. Unlike
 * `formatPrice` (a "from $X" marketing helper that suppresses ≤ 0), this never
 * hides a value — a $0.00 balance or a $12.50 fee must show verbatim.
 */
export function formatMoney(
  amount: number | null | undefined,
  currency = "USD",
): string {
  const value = amount == null || Number.isNaN(amount) ? 0 : amount;
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `${currency} ${value.toFixed(2)}`;
  }
}

/**
 * Resolve the display price for a tier using the pricing priority:
 * 1. defaultPrice (if > 0)
 * 2. Minimum group price (if available)
 * 3. 0 (fallback, should be displayed as empty)
 */
export function resolveTierPrice(tier: {
  defaultPrice?: number | null;
  groupPrices?: Array<{ price: number }>;
}): number {
  if (tier.defaultPrice && tier.defaultPrice > 0) {
    return tier.defaultPrice;
  }
  if (tier.groupPrices && tier.groupPrices.length > 0) {
    return Math.min(...tier.groupPrices.map((gp) => gp.price));
  }
  return 0;
}

/**
 * Cheapest price for a tier, preferring the lowest group-price bracket over
 * the flat default — the inverse priority of `resolveTierPrice` (used for
 * per-departure rows, where the group discount is the more relevant number).
 */
export function lowestTierPrice(tier: {
  defaultPrice?: number | null;
  groupPrices?: Array<{ price: number }>;
}): number {
  if (tier.groupPrices && tier.groupPrices.length > 0) {
    return Math.min(...tier.groupPrices.map((gp) => gp.price));
  }
  return resolveTierPrice(tier);
}

export function formatDuration(
  value: number | null | undefined,
  unit?: string,
): string {
  if (!value || value <= 0) return "";
  const u =
    unit === "hours"
      ? value === 1
        ? "hour"
        : "hours"
      : value === 1
        ? "day"
        : "days";
  return `${value} ${u}`;
}

export function formatDate(input: string | Date | null | undefined): string {
  if (!input) return "";
  const date = typeof input === "string" ? new Date(input) : input;
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(date);
}

export function plainText(html?: string | null, max = 220): string {
  if (!html) return "";
  const stripped = html
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return stripped.length > max
    ? `${stripped.slice(0, max - 1).trimEnd()}…`
    : stripped;
}

/**
 * Pull the first `max` list-item texts out of a tier's includes/excludes HTML
 * (shaped `<ul><li><p>…</p></li></ul>`), as plain strings for compact
 * "highlight" bullets on tier cards. Tags are stripped (no raw HTML rendered),
 * blanks/dupes dropped. Returns `[]` when there are no `<li>`s — callers fall
 * back to the tier abstract.
 */
export function tierHighlights(html?: string | null, max = 3): string[] {
  if (!html) return [];
  const items = html.match(/<li[^>]*>([\s\S]*?)<\/li>/gi);
  if (!items) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of items) {
    const text = plainText(raw, 90);
    const key = text.toLowerCase();
    if (!text || seen.has(key)) continue;
    seen.add(key);
    out.push(text);
    if (out.length >= max) break;
  }
  return out;
}

type ClassDictionary = Record<string, boolean | null | undefined>;
type ClassValue =
  | string
  | number
  | false
  | null
  | undefined
  | ClassDictionary
  | ClassValue[];

const TEXT_SIZE_CLASSES = new Set([
  "text-xs",
  "text-sm",
  "text-base",
  "text-lg",
  "text-xl",
  "text-2xl",
  "text-3xl",
  "text-4xl",
  "text-5xl",
  "text-6xl",
  "text-7xl",
  "text-8xl",
  "text-9xl",
]);

// Tailwind Typography's own size scale — a separate axis from TEXT_SIZE_CLASSES
// (text-sm etc.), and mutually exclusive with itself: `prose-sm` and `prose-lg`
// target the same `.prose` sizing, so a later one should win, not stack.
const PROSE_SIZE_CLASSES = new Set([
  "prose-sm",
  "prose-base",
  "prose-lg",
  "prose-xl",
  "prose-2xl",
]);

const BORDER_STYLES = new Set([
  "border-solid",
  "border-dashed",
  "border-dotted",
  "border-double",
  "border-hidden",
  "border-none",
]);

const BORDER_WIDTHS = new Set([
  "border",
  "border-0",
  "border-2",
  "border-4",
  "border-8",
]);

function flattenClassValues(
  values: ClassValue[],
  classes: string[] = [],
): string[] {
  for (const value of values) {
    if (!value) continue;

    if (typeof value === "string" || typeof value === "number") {
      classes.push(...String(value).trim().split(/\s+/).filter(Boolean));
      continue;
    }

    if (Array.isArray(value)) {
      flattenClassValues(value, classes);
      continue;
    }

    for (const [className, enabled] of Object.entries(value)) {
      if (enabled)
        classes.push(...className.trim().split(/\s+/).filter(Boolean));
    }
  }

  return classes;
}

function splitModifiers(
  className: string,
): [modifiers: string, utility: string] {
  let bracketDepth = 0;
  let lastSeparator = -1;

  for (let index = 0; index < className.length; index += 1) {
    const char = className[index];

    if (char === "[") bracketDepth += 1;
    if (char === "]") bracketDepth = Math.max(0, bracketDepth - 1);
    if (char === ":" && bracketDepth === 0) lastSeparator = index;
  }

  if (lastSeparator === -1) return ["", className];
  return [
    className.slice(0, lastSeparator),
    className.slice(lastSeparator + 1),
  ];
}

function stripUtilityPrefix(utility: string): string {
  return utility.replace(/^!/, "").replace(/^-/, "");
}

function classGroup(utility: string): string {
  const normalized = stripUtilityPrefix(utility);

  if (/^(static|fixed|absolute|relative|sticky)$/.test(normalized))
    return "position";
  if (
    /^(block|inline-block|inline|flex|inline-flex|grid|inline-grid|hidden)$/.test(
      normalized,
    )
  )
    return "display";
  if (/^(visible|invisible|collapse)$/.test(normalized)) return "visibility";
  if (/^z-/.test(normalized)) return "z";

  const spacing = normalized.match(
    /^(m|mx|my|ms|me|mt|mr|mb|ml|p|px|py|ps|pe|pt|pr|pb|pl)-/,
  );
  if (spacing) return spacing[1];

  const inset = normalized.match(
    /^(inset|inset-x|inset-y|top|right|bottom|left)-/,
  );
  if (inset) return inset[1];

  const sizing = normalized.match(/^(w|min-w|max-w|h|min-h|max-h)-/);
  if (sizing) return sizing[1];

  if (/^overflow-x-/.test(normalized)) return "overflow-x";
  if (/^overflow-y-/.test(normalized)) return "overflow-y";
  if (/^overflow-/.test(normalized)) return "overflow";

  if (/^flex-(row|row-reverse|col|col-reverse)$/.test(normalized))
    return "flex-direction";
  if (/^flex-(wrap|wrap-reverse|nowrap)$/.test(normalized)) return "flex-wrap";
  if (/^flex-\d|^flex-\[|^flex-(none|auto|initial|1)$/.test(normalized))
    return "flex";
  if (/^grow/.test(normalized)) return "grow";
  if (/^shrink/.test(normalized)) return "shrink";
  if (/^basis-/.test(normalized)) return "basis";

  const alignment = normalized.match(
    /^(items|justify|content|self|place-items|place-content|place-self)-/,
  );
  if (alignment) return alignment[1];

  const gap = normalized.match(/^(gap|gap-x|gap-y)-/);
  if (gap) return gap[1];

  if (/^rounded($|-)/.test(normalized))
    return (
      normalized.match(
        /^rounded(?:-(s|e|t|r|b|l|ss|se|ee|es|tl|tr|br|bl))?-/,
      )?.[1] ?? "rounded"
    );

  if (BORDER_STYLES.has(normalized)) return "border-style";
  if (
    BORDER_WIDTHS.has(normalized) ||
    /^border(?:-[xysetrbl])?-(?:0|2|4|8|\[)/.test(normalized)
  ) {
    return normalized.match(/^border-([xysetrbl])-/)?.[1] ?? "border-width";
  }
  if (/^border(?:-[xysetrbl])?-/.test(normalized))
    return normalized.match(/^border-([xysetrbl])-/)?.[1] ?? "border-color";

  if (/^bg-/.test(normalized)) return "background";
  if (TEXT_SIZE_CLASSES.has(normalized) || /^text-\[/.test(normalized))
    return "text-size";
  if (PROSE_SIZE_CLASSES.has(normalized)) return "prose-size";
  // Same group as bare "prose" below: prose-invert is a full replacement for
  // it (there's no separate .prose-invert stylesheet to layer on top of
  // .prose — see globals.css), so whichever comes later should win outright.
  if (normalized === "prose" || normalized === "prose-invert") return "prose";
  if (/^text-(left|center|right|justify|start|end)$/.test(normalized))
    return "text-align";
  if (/^text-/.test(normalized)) return "text-color";
  if (/^font-/.test(normalized)) return "font";
  if (/^leading-/.test(normalized)) return "leading";
  if (/^tracking-/.test(normalized)) return "tracking";
  if (/^whitespace-/.test(normalized)) return "whitespace";

  if (/^ring-offset-(?:0|1|2|4|8|\[)/.test(normalized))
    return "ring-offset-width";
  if (/^ring-offset-/.test(normalized)) return "ring-offset-color";
  if (/^ring(?:-\d|\[|$)/.test(normalized)) return "ring-width";
  if (/^ring-/.test(normalized)) return "ring-color";

  if (/^shadow/.test(normalized)) return "shadow";
  if (/^opacity-/.test(normalized)) return "opacity";
  if (/^cursor-/.test(normalized)) return "cursor";
  if (/^pointer-events-/.test(normalized)) return "pointer-events";
  if (/^appearance-/.test(normalized)) return "appearance";

  const transforms = normalized.match(
    /^(translate-x|translate-y|scale-x|scale-y|scale|rotate|skew-x|skew-y)-/,
  );
  if (transforms) return transforms[1];

  if (/^transition/.test(normalized)) return "transition";
  if (/^duration-/.test(normalized)) return "duration";
  if (/^ease-/.test(normalized)) return "ease";

  return normalized;
}

export function cn(...parts: ClassValue[]): string {
  const classes = flattenClassValues(parts);
  const classIndexes = new Map<string, number>();
  const merged: string[] = [];

  for (const className of classes) {
    const [modifiers, utility] = splitModifiers(className);
    const important = utility.startsWith("!") ? "!" : "";
    const group = `${modifiers}|${important}${classGroup(utility)}`;
    const existingIndex = classIndexes.get(group);

    if (existingIndex !== undefined) {
      merged[existingIndex] = "";
    }

    classIndexes.set(group, merged.length);
    merged.push(className);
  }

  return merged.filter(Boolean).join(" ");
}
